/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable max-len */
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useAuth } from "../../modules/auth/useAuth";
import { useTranslation } from "react-i18next";
import {
  Dropdown,
  DropdownButton,
  Button,
  Modal,
  Select,
  Spinner,
} from "@xfrontend/xui-react-plus";
import PanelProvider from "#/modules/common/PanelProvider";
import ApplicationCard from "./components/ApplicationCard";
import { getApplicationTokens } from "./slice";
import { ApplicationCardData } from "./types";
import { RootState } from "#/types/RootState";
import { POLICY_DISPLAY_NAMES } from "#/types/xecguard";
import { getPolicyTagInfo } from "#/i18n/hooks";
import { getDashboardData } from "#/modules/dashboard/redux/actionCreator";
import { DashboardItem } from "#/apis/infra/getDashboard";
import { createServiceToken } from "#/apis/infra/getServiceTokens";
import { toast } from "#/infra/Toastify";

// Removed ApplicationsContainer and MainContent

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const autoWidthSelectStyle = {
  placeholder: (base: any) => ({ ...base, whiteSpace: "nowrap" }),
  singleValue: (base: any) => ({ ...base, whiteSpace: "nowrap" }),
};

const ApplicationsPanel = styled.div`
  flex: 1;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: var(--x-gray-200);
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.div`
  color: var(--x-red-light);
  text-align: center;
  padding: 40px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: var(--x-red, #ff595a);
`;

const FormInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid var(--x-purple-500, #494358);
  background: var(--x-purple-600, #3a3646);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  box-sizing: border-box;

  &::placeholder {
    color: var(--x-gray-500);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid var(--x-purple-500, #494358);
  background: var(--x-purple-600, #3a3646);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;

  &::placeholder {
    color: var(--x-gray-500);
  }
`;

const extractViolationTypes = (
  policyCounts: Record<string, number> | undefined,
  customPolicyLabel: string,
  getTag: (policyName: string) => { label: string; tagIndex: number },
): { label: string; tagIndex: number }[] => {
  if (!policyCounts) return [];

  const known: { label: string; tagIndex: number }[] = [];
  let hasCustom = false;

  Object.keys(policyCounts).forEach((key) => {
    if (policyCounts[key] <= 0) return;
    if (POLICY_DISPLAY_NAMES[key]) {
      known.push(getTag(key));
    } else {
      hasCustom = true;
    }
  });

  if (hasCustom) {
    known.push({ label: customPolicyLabel, tagIndex: 3 });
  }

  return known;
};

const Applications = (): JSX.Element => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const { t } = useTranslation("applications");

  const applicationsState = useSelector(
    (state: RootState) => state.applications,
  );
  const tokens = applicationsState?.tokens ?? {
    data: [],
    loading: false,
    error: null,
  };
  const dashboardLoading: boolean = useSelector(
    (state: RootState) => state.dashboard.dashboardData.loading,
  );
  const dashboardDataRaw = useSelector(
    (state: RootState) => state.dashboard.dashboardData.results,
  );
  const dashboardData: DashboardItem[] = Array.isArray(dashboardDataRaw) ? dashboardDataRaw : [];

  const [selectedGroup, setSelectedGroup] = useState("");
  const [searchName, setSearchName] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    group: "",
    description: "",
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [hasCopiedKey, setHasCopiedKey] = useState(false);

  const handleCreateToken = async () => {
    if (!auth.user?.access_token || !createForm.name) return;
    setIsCreating(true);
    const result = await createServiceToken({
      token: auth.user.access_token,
      data: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        service_token_name: createForm.name,
        division: createForm.group,
        description: createForm.description,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().replace(/\.\d{3}Z$/, "Z"),
      },
    });
    setIsCreating(false);
    if (result.success && result.data) {
      setCreatedToken(result.data.token ?? "");
      dispatch(getApplicationTokens({ accessToken: auth.user.access_token }));
    }
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateForm({
      name: "", group: "", description: "",
    });
    setCreatedToken(null);
    setHasCopiedKey(false);
  };

  // Fetch tokens and dashboard data on mount
  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.access_token) {
      dispatch(getApplicationTokens({ accessToken: auth.user.access_token }));
      dispatch(getDashboardData({ accessToken: auth.user.access_token, period: 7 }));
    }
  }, [auth.isAuthenticated, auth.user?.access_token, dispatch]);

  // Build lookup: service_token_name → DashboardItem
  const dashboardMap = useMemo(() => {
    const map: Record<string, DashboardItem> = {};
    dashboardData.forEach((item) => {
      map[item.service_token_name] = item;
    });
    return map;
  }, [dashboardData]);

  // Extract unique groups (divisions) for dropdown
  const groups = useMemo(() => {
    const divs = new Set<string>();
    tokens.data?.forEach((t) => { if (t.division) divs.add(t.division); });
    return Array.from(divs).sort();
  }, [tokens.data]);

  // Transform data for ApplicationCard
  const applicationCards: ApplicationCardData[] = tokens.data?.map((token) => {
    const dashItem = dashboardMap[token.service_token_name];
    const summary = dashItem?.summary;

    return {
      id: token.service_token_id,
      title: token.service_token_name,
      organization: token.division || t("uncategorized"),
      status:
          token.service_token_status === "enable" ? t("status.enabled") : t("status.disabled"),
      secureScore: summary?.security_score ?? 0,
      lastActive: "–",
      stats: {
        unsafeRequests: {
          value: summary?.request_unsafe_count ?? 0,
          change: "+0%",
        },
        totalRequests: {
          value: summary?.request_count ?? 0,
          change: "+0%",
        },
      },
      violationTypes: extractViolationTypes(
        summary?.policies_request_counts,
        t("card.customPolicy"),
        (name) => getPolicyTagInfo(name),
      ),
      maxRiskLevel: Number(
        ["4", "3", "2", "1"].find(
          (risk) => (summary?.unsafe_request_counts_by_max_risk?.[risk] || 0) > 0,
        ) || 0,
      ),
      statsLoading: dashboardLoading,
    };
  }) || [];

  // Filter cards by group and search name
  const filteredCards = useMemo(() => applicationCards.filter((card) => {
    if (selectedGroup && card.organization !== selectedGroup) return false;
    if (searchName && !card.title.toLowerCase().includes(searchName.toLowerCase())) return false;
    return true;
  }), [applicationCards, selectedGroup, searchName]);

  if (tokens.loading) {
    return (
      <PanelProvider>
        <div style={{ padding: "12px" }}>
          <ApplicationsPanel>
            <PanelHeader>
              <Title>{t("pageTitle")}</Title>
            </PanelHeader>
            <div style={{
              display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0",
            }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          </ApplicationsPanel>
        </div>
      </PanelProvider>
    );
  }

  if (tokens.error) {
    return (
      <PanelProvider>
        <div style={{ padding: "12px" }}>
          <ApplicationsPanel>
            <PanelHeader>
              <Title>{t("pageTitle")}</Title>
            </PanelHeader>
            <ErrorMessage>
              {t("error.loading", { message: tokens.error })}
            </ErrorMessage>
          </ApplicationsPanel>
        </div>
      </PanelProvider>
    );
  }

  return (
    <PanelProvider>
      <div style={{ padding: "12px" }}>
        <ApplicationsPanel>
          <PanelHeader>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Title>{t("pageTitle")}</Title>
              <div style={{
                display: "flex", alignItems: "center", gap: "8px", marginLeft: 36,
              }}
              >
                <div style={{ minWidth: 252, width: "fit-content" }}>
                  <Select
                    name="ReactSelect"
                    placeholder={t("filters.groupPlaceholder")}
                    options={groups.map((g) => ({ value: g, label: g }))}
                    size="md"
                    isClearable
                    customStyle={autoWidthSelectStyle}
                    value={selectedGroup ? { value: selectedGroup, label: selectedGroup } : undefined}
                    onChange={(opt: { value: string; label: string } | null) => {
                      setSelectedGroup(opt?.value || "");
                    }}
                  />
                </div>
                <div style={{ minWidth: 252, width: "fit-content" }}>
                  <Select
                    name="ReactSelect"
                    placeholder={t("filters.namePlaceholder")}
                    options={applicationCards.map((app) => ({ value: app.title, label: app.title }))}
                    size="md"
                    isClearable
                    withSearchIndicator
                    customStyle={autoWidthSelectStyle}
                    value={searchName ? { value: searchName, label: searchName } : undefined}
                    onChange={(opt: { value: string; label: string } | null) => {
                      setSearchName(opt?.value || "");
                    }}
                  />
                </div>
              </div>
            </div>
            <DropdownButton title={t("functions")} variant="secondary" size="sm" align="end">
              <Dropdown.Item onClick={() => setIsCreateModalOpen(true)}>
                {t("createOption")}
              </Dropdown.Item>
            </DropdownButton>
          </PanelHeader>
          <CardsGrid>
            {filteredCards.map((app) => (
              <ApplicationCard key={app.id} data={app} />
            ))}
          </CardsGrid>
        </ApplicationsPanel>

        <Modal
          title={t("createModal.title")}
          show={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          showCloseBtn={!createdToken || hasCopiedKey}
          restoreFocus={false}
        >
          <Modal.Body>
            <div style={{
              fontWeight: 700, fontSize: 18, color: "var(--x-gray-200)", marginBottom: 16,
            }}
            >
              {t("createModal.subtitle")}
            </div>
            <FormGroup>
              <FormLabel>
                {t("createModal.nameLabel")}
                <RequiredMark>*</RequiredMark>
              </FormLabel>
              <FormInput
                placeholder={t("createModal.namePlaceholder")}
                maxLength={50}
                value={createForm.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCreateForm((prev) => ({ ...prev, name: e.target.value }));
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>{t("createModal.groupLabel")}</FormLabel>
              <FormInput
                placeholder={t("createModal.groupPlaceholder")}
                value={createForm.group}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCreateForm((prev) => ({ ...prev, group: e.target.value }));
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>{t("createModal.descriptionLabel")}</FormLabel>
              <FormTextarea
                placeholder={t("createModal.descriptionPlaceholder")}
                value={createForm.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setCreateForm((prev) => ({ ...prev, description: e.target.value }));
                }}
              />
            </FormGroup>
            {!createdToken && (
              <Button
                variant="primary"
                onClick={handleCreateToken}
                disabled={isCreating || !createForm.name}
              >
                {isCreating ? t("createModal.creating") : t("createModal.createBtn")}
              </Button>
            )}

            {createdToken && (
              <div style={{ marginTop: 24 }}>
                <FormLabel>{t("createModal.tokenLabel")}</FormLabel>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <FormInput
                    readOnly
                    value={createdToken}
                    style={{ flex: 1, fontFamily: "\"Roboto Mono\", monospace" }}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(createdToken);
                      setHasCopiedKey(true);
                      toast.copied(t("createModal.tokenCopied"));
                    }}
                  >
                    {t("createModal.copyBtn")}
                  </Button>
                </div>
                <div style={{
                  marginTop: 8, fontSize: 13, color: "var(--x-yellow, #f2c94c)",
                }}
                >
                  {t("createModal.securityWarning")}
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCreateModal} disabled={createdToken != null && !hasCopiedKey}>
              {t("createModal.close")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </PanelProvider>
  );
};

export default Applications;
